import React, {
	Dispatch,
	SetStateAction,
	useCallback,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	MeasurementSystem,
	PairedVelavuDevice,
	VelavuDevice,
	VelavuSite,
} from "velavu-js-api";
import AnalyticsStatType from "../../data/analytics-stat-type";
import IconVelavuAnalytics from "../../dynamicicons/icon-velavu-analytics";
import SectionTitle from "../../elements/section-title";
import VelavuTabSwitcher from "../../elements/velavu-tab-switcher";
import { useToggleable } from "../../helper/hook-helper";
import { makeStyleable } from "../../helper/icon-helper";
import AnalyticsAssetsNavigation from "./analytics-assets-navigation";
import AnalyticsSidebarFilter from "./analytics-sidebar-filter";
import { filterAnalyticsDevices } from "./analytics-sidebar-helper";
import AnalyticsSidebarSites from "./analytics-sidebar-sites";
import { useSortedCategorizations } from "./analytics-sidebar-state";
import * as styles from "./analytics-sidebar.module.scss";
import { AnalyticsTargetTab } from "./analytics-target-tab";
import AnalyticsSidebarDevicesList from "./analytics-sidebar-devices-list";

export interface AnalyticsSidebarProps {
	sites: VelavuSite[] | undefined;
	statType: AnalyticsStatType;
	measurementSystem: MeasurementSystem;

	selectedTab: AnalyticsTargetTab;
	setSelectedTab: React.Dispatch<React.SetStateAction<AnalyticsTargetTab>>;

	tags: PairedVelavuDevice[] | undefined;
	selectedTags: string[];
	setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;

	anchors: VelavuDevice[] | undefined;
	selectedAnchors: string[];
	setSelectedAnchors: React.Dispatch<React.SetStateAction<string[]>>;
	selectedSiteID?: string;
	setSelectedSiteID: Dispatch<SetStateAction<string | undefined>>;
}

export default function AnalyticsSidebar(props: AnalyticsSidebarProps) {
	const { t } = useTranslation();

	//Filtering state
	const [filterCategorizations, setFilterCategorizations] = useState<
		string[]
	>([]);
	const [filterStatus, setFilterStatus] = useState<boolean | undefined>(
		undefined,
	);
	const [searchText, setSearchText] = useState("");
	const [selectedFloorID, setSelectedFloorID] = useState<string | undefined>(
		undefined,
	);

	const sortedCategorizations = useSortedCategorizations({
		devices: props.tags,
		selectedCategorizations: filterCategorizations,
	});

	//Filter tags
	const filteredTags = useMemo((): PairedVelavuDevice[] | undefined => {
		if (props.tags === undefined) {
			return undefined;
		}

		return filterAnalyticsDevices(props.tags, {
			siteID: props.selectedSiteID,
			floorID: selectedFloorID,
			categorization:
				filterCategorizations.length === 0
					? undefined
					: filterCategorizations,
			status: filterStatus,
			searchQuery: searchText,
		});
	}, [
		filterStatus,
		props.tags,
		props.sites,
		searchText,
		selectedFloorID,
		props.selectedSiteID,
		filterCategorizations,
	]);

	//Filter anchors
	const filteredAnchors = useMemo((): VelavuDevice[] | undefined => {
		if (props.anchors === undefined) {
			return undefined;
		}

		return filterAnalyticsDevices(props.anchors, {
			siteID: props.selectedSiteID,
			floorID: selectedFloorID,
			status: filterStatus,
			searchQuery: searchText,
		});
	}, [
		props.anchors,
		props.sites,
		props.selectedSiteID,
		searchText,
		selectedFloorID,
		filterStatus,
	]);

	const [isSiteSelector, showSiteSelector, hideSiteSelector] =
		useToggleable();

	const updateSiteSelection = useCallback(
		(siteID: string | undefined) => {
			//Forward the event to the parent
			props.setSelectedSiteID(siteID);

			//Reset the floor selection
			setSelectedFloorID(undefined);

			//Close the site selector
			hideSiteSelector();
		},
		[props.setSelectedSiteID, setSelectedFloorID, hideSiteSelector],
	);

	const [isSearching, openSearch, closeSearch] = useToggleable();
	const [showFilterMenu, setShowFilterMenu] = useState(false);
	const toggleFilterMenu = useCallback(
		() => setShowFilterMenu((it) => !it),
		[setShowFilterMenu],
	);

	const selectedSite = useMemo(() => {
		return props.sites?.find((site) => site.id === props.selectedSiteID);
	}, [props.sites, props.selectedSiteID]);

	return (
		<>
			<div className={styles.header}>
				<SectionTitle
					icon={makeStyleable(IconVelavuAnalytics)}
					label={t("sidebar.nav.analytics")}
				/>
			</div>

			{isSiteSelector ? (
				<AnalyticsSidebarSites
					sites={props.sites}
					onSelectSite={updateSiteSelection}
				/>
			) : (
				<React.Fragment>
					<AnalyticsAssetsNavigation
						isSearching={isSearching}
						openSearch={openSearch}
						finishSearch={closeSearch}
						searchText={searchText}
						setSearchText={setSearchText}
						selectedSite={selectedSite}
						onOpenSiteSelector={showSiteSelector}
						selectedFloorID={selectedFloorID}
						setSelectedFloorID={setSelectedFloorID}
						isFiltering={false}
						isShowFilterMenu={showFilterMenu}
						onToggleFilterMenu={toggleFilterMenu}
					/>
					<VelavuTabSwitcher
						selectedKey={props.selectedTab}
						onSelectKey={props.setSelectedTab}
						labels={{
							[AnalyticsTargetTab.Assets]: t(
								"analytics.tab_assets",
							),
							[AnalyticsTargetTab.Anchors]: t(
								"analytics.tab_anchors",
							),
						}}
					>
						<AnalyticsSidebarFilter
							expanded={showFilterMenu}
							allCategorizations={
								props.selectedTab === AnalyticsTargetTab.Assets
									? (sortedCategorizations ?? [])
									: []
							}
							selectedCategorizations={filterCategorizations}
							onSetSelectedCategorizations={
								setFilterCategorizations
							}
							filterStatus={filterStatus}
							setFilterStatus={setFilterStatus}
						/>
						{props.selectedTab === AnalyticsTargetTab.Assets && (
							<AnalyticsSidebarDevicesList
								className={styles.list}
								devices={filteredTags}
								selectedDevices={props.selectedTags}
								setSelectedDevices={props.setSelectedTags}
								statType={props.statType}
								measurementSystem={props.measurementSystem}
							/>
						)}
						{props.selectedTab === AnalyticsTargetTab.Anchors && (
							<AnalyticsSidebarDevicesList
								devices={filteredAnchors}
								selectedDevices={props.selectedAnchors}
								setSelectedDevices={props.setSelectedAnchors}
								statType={props.statType}
								measurementSystem={props.measurementSystem}
							/>
						)}
					</VelavuTabSwitcher>
				</React.Fragment>
			)}
		</>
	);
}
